import React, { useEffect, useRef, useState } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { searchService } from "../../services/api";
import CustomDateTimePicker from "../common/CustomDateTimePicker";

const MeetingFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  mode = "create",
  initialData = {},
}) => {
  const dialogRef = useRef(null);
  const [formData, setFormData] = useState({
    schedule_for: initialData.schedule_for || "",
    topic: initialData.topic || "",
    start_time: initialData.start_time
      ? initialData.start_time.slice(0, 16)
      : "", // datetime-local format
    end_time: initialData.end_time ? initialData.end_time.slice(0, 16) : "", // datetime-local format
    duration: initialData.duration || 30,
    timezone:
      initialData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    agenda: initialData.agenda || "",
  });

  const [entityType, setEntityType] = useState("agents"); // 'agents' or 'club'
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebounce(searchTerm, 400);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      if (!debouncedTerm) {
        setResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const res = await searchService.search({
          q: debouncedTerm,
          type: entityType,
          page: 1,
        });

        console.log("res", res);
        if (res.success && res.data) {
          // Updated to handle new response structure
          if (entityType === "club") {
            const resultData = res.data?.results?.clubs || [];
            setResults(resultData);
          } else {
            const resultsData = res.data.results?.[entityType] || [];
            setResults(resultsData);
          }
        } else {
          setResults([]);
        }
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      }
      setIsSearching(false);
    };
    fetchResults();
  }, [debouncedTerm, entityType]);

  // Calculate duration when start_time or end_time changes
  useEffect(() => {
    if (formData.start_time && formData.end_time) {
      const start = new Date(formData.start_time);
      const end = new Date(formData.end_time);
      const diffInMinutes = Math.round((end - start) / (1000 * 60));
      if (diffInMinutes > 0) {
        setFormData((prev) => ({ ...prev, duration: diffInMinutes }));
      }
    }
  }, [formData.start_time, formData.end_time]);

  // Validation
  useEffect(() => {
    const errs = {};
    if (!formData.schedule_for) errs.schedule_for = "Select a guest";
    if (!formData.topic.trim()) errs.topic = "Topic required";
    if (formData.topic.length > 100) errs.topic = "Max 100 characters";
    const startDate = new Date(formData.start_time);
    const endDate = new Date(formData.end_time);
    if (isNaN(startDate)) errs.start_time = "Start time required";
    else if (startDate < new Date(Date.now() + 5 * 60000))
      errs.start_time = "Must be at least 5 minutes in future";
    if (isNaN(endDate)) errs.end_time = "End time required";
    else if (endDate <= startDate)
      errs.end_time = "End time must be after start time";
    if (formData.duration < 15 || formData.duration > 240)
      errs.duration = "15–240 min";
    if (formData.duration % 15 !== 0) errs.duration = "Must be multiple of 15";
    setErrors(errs);
  }, [formData]);

  const handleSelectUser = (user) => {
    setFormData({ ...formData, schedule_for: user.account_id });
    setSearchTerm(user.name || user.email || user.account_id);
    setResults([]);
    setShowSearchResults(false);
  };

  // Focus trapping
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
      setShowEntityDropdown(false);
      setShowSearchResults(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSearchResults(true);
  };

  const handleClickOutside = (e) => {
    // Close dropdowns when clicking outside
    if (!e.target.closest(".search-container")) {
      setShowEntityDropdown(false);
      setShowSearchResults(false);
    }
  };

  const handleSubmit = (e) => {
    console.log("handleSubmit", e);
    e.preventDefault();
    setShowErrors(true);
    console.log("errors", errors);
    if (Object.keys(errors).length === 0) {
      console.log("errors are zero");
      // Calculate duration for API call
      const payload = { ...formData };
      if (formData.start_time && formData.end_time) {
        const start = new Date(formData.start_time);
        const end = new Date(formData.end_time);
        payload.duration = Math.round((end - start) / (1000 * 60));
      }
      console.log("Create meeting payload", payload);
      onSubmit(payload);
    }
  };

  if (!isOpen) return null;

  // Convert date to local ISO string without timezone conversion
  const toLocalISOString = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onKeyDown={handleKeyDown}
      onClick={handleClickOutside}
      aria-modal="true"
      role="dialog"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 overflow-auto max-h-full focus:outline-none"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          {mode === "edit" ? "Edit Meeting" : "New meeting"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Create meeting with section */}
          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              Create meeting with
            </label>
            <div className="relative search-container">
              {/* Entity Type Dropdown */}
              <div className="relative mb-3">
                <button
                  type="button"
                  onClick={() => setShowEntityDropdown(!showEntityDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 border border-[#E9E9E9] rounded-lg bg-white text-[#000000] text-base font-medium"
                >
                  <span>
                    {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                  </span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {showEntityDropdown && (
                  <div className="absolute top-full left-0 right-0 z-10 bg-white border border-[#E9E9E9] rounded-lg shadow-lg mt-1">
                    {["agents", "club"].map((t) => (
                      <button
                        type="button"
                        key={t}
                        onClick={() => {
                          setEntityType(t);
                          setShowEntityDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 text-base font-medium text-[#000000]"
                      >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Input */}
              <input
                type="text"
                placeholder="Select club or agent"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg text-base font-medium placeholder-[#00000099] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {/* Search Results */}
              {searchTerm && showSearchResults && (
                <div className="absolute top-full left-0 right-0 z-10 max-h-56 overflow-y-auto border border-[#E9E9E9] rounded-lg mt-1 bg-white shadow-lg">
                  {isSearching ? (
                    <p className="p-3 text-base text-gray-500">Searching…</p>
                  ) : !Array.isArray(results) || results.length === 0 ? (
                    <p className="p-3 text-base text-gray-500">No results</p>
                  ) : (
                    results.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 p-2 ">
                        {u.image_path ? (
                          <img
                            src={u.image_path}
                            alt={u.name}
                            className="w-7 h-7 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-500 text-sm">
                              {u.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <button
                          type="button"
                          key={u.id}
                          onClick={() => handleSelectUser(u)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 text-base font-medium text-[#000000]"
                        >
                          {u.name || u.email}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {showErrors && errors.schedule_for && (
              <p className="text-sm text-red-600 mt-1">{errors.schedule_for}</p>
            )}
          </div>

          {/* Topic */}
          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              Topic
            </label>
            <input
              type="text"
              name="topic"
              required
              value={formData.topic}
              onChange={handleChange}
              placeholder="Enter meeting topic"
              className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg text-base font-medium placeholder-[#00000099] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {showErrors && errors.topic && (
              <p className="text-sm text-red-600 mt-1">{errors.topic}</p>
            )}
          </div>

          {/* Time & Date Section */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <CustomDateTimePicker
                  label="Start Time"
                  value={
                    formData.start_time ? new Date(formData.start_time) : null
                  }
                  onChange={(date) => {
                    if (date) {
                      const localISOString = toLocalISOString(date);
                      setFormData((prev) => ({
                        ...prev,
                        start_time: localISOString,
                      }));
                    } else {
                      setFormData((prev) => ({ ...prev, start_time: "" }));
                    }
                  }}
                  showTimeSelect={true}
                  timeIntervals={15}
                  minDate={new Date()}
                  required
                  closeOnClickOutside={false}
                  error={
                    showErrors && errors.start_time ? errors.start_time : null
                  }
                  className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <CustomDateTimePicker
                  label="End Time"
                  value={formData.end_time ? new Date(formData.end_time) : null}
                  onChange={(date) => {
                    if (date) {
                      const localISOString = toLocalISOString(date);
                      setFormData((prev) => ({
                        ...prev,
                        end_time: localISOString,
                      }));
                    } else {
                      setFormData((prev) => ({ ...prev, end_time: "" }));
                    }
                  }}
                  showTimeSelect={true}
                  timeIntervals={15}
                  minDate={
                    formData.start_time
                      ? new Date(formData.start_time)
                      : new Date()
                  }
                  required
                  closeOnClickOutside={false}
                  error={showErrors && errors.end_time ? errors.end_time : null}
                  className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {/* Duration Display */}
            {formData.duration && (
              <div className="mt-2 text-sm text-gray-600">
                Duration: {formData.duration} minutes
              </div>
            )}
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              Timezone
            </label>
            <input
              type="text"
              name="timezone"
              required
              value={formData.timezone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Agenda */}
          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              Agenda
            </label>
            <textarea
              name="agenda"
              rows="3"
              value={formData.agenda}
              onChange={handleChange}
              placeholder="Enter meeting agenda"
              className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg text-base font-medium placeholder-[#00000099] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-base font-medium text-[#000000] border border-[#E6E6E6] rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              type="submit"
              className="px-6 py-2 text-base font-medium text-white bg-[#2283FF] rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {mode === "edit" ? "Save" : "Create meeting"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MeetingFormModal;
